import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';
import { format, parseISO } from 'date-fns';
import ProFormaGenericTable from 'views/ProFormaTable/components/ProFormaGenericTable/ProFormaGenericTable';
import { ProFormaGenericRow } from 'views/ProFormaTable/components/ProFormaGenericTable/types';
import { NSInputNumberStepper, NSLabel } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import { IOptionAnyValue } from 'bricks/types';
import { useCustomScroll } from 'views/ProFormaTable/hooks/useCustomScroll';
import {
    createCashFlowData, formatCurrencyWithParentheses, getPeriods,
} from 'views/ProFormaTable/helpers';
import { INameValue } from 'views/ProFormaTable/types';
import {
    OPERATIONS_EXPENSES_WS_HANDLERS, OperationExpenseType,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { IncomeCalculationType, ProFormaModelIds } from 'constants/proForma';
import {
    CAPITAL_RESERVES_OPTIONS,
    OPERATING_EXPENSES_LABEL,
    CAPITAL_EXPENDITURES_LABEL,
    CAPITAL_EXPENDITURES_COLUMNS,
    CASH_FLOW_COLUMNS,
    NET_OPERATION_INCOME_COLUMNS,
    OPERATING_EXPENSES_COLUMNS,
} from './constants';
import './ProFormaExpenses.scss';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS } from '../ProFormaIncomeTable/OfficeIndustrialRetailIncomeTable/OfficeIndustrialRetailIncomeTable';

interface ColumnTransform {
    key: string;
    field: string;
    header: string;
}

const getValuePerUnitHeader = (proFormaModelId: ProFormaModelIds, isIncomeCalculationTypeTenants: boolean): string => {
    if (proFormaModelId === ProFormaModelIds.APARTMENT) {
        return '$/Unit/year';
    }
    if ([ProFormaModelIds.RETAIL, ProFormaModelIds.OFFICE, ProFormaModelIds.INDUSTRIAL].includes(proFormaModelId)) {
        return isIncomeCalculationTypeTenants ? '$/RSF/year' : '$/SF/year';
    }
    return '$/SF/year';
};

const transformCostPerUnitColumn = (
    proFormaModelId: ProFormaModelIds,
    isIncomeCalculationTypeTenants: boolean,
): ColumnTransform => {
    const isTenantBasedModel = [ProFormaModelIds.RETAIL, ProFormaModelIds.OFFICE, ProFormaModelIds.INDUSTRIAL].includes(proFormaModelId);

    return {
        key: isTenantBasedModel ? 'valuePerSfPerYear' : 'costPerUnitPerYear',
        field: isTenantBasedModel ? 'valuePerSfPerYear' : 'costPerUnitPerYear',
        header: getValuePerUnitHeader(proFormaModelId, isIncomeCalculationTypeTenants),
    };
};

const ProFormaExpenses = () => {
    const {
        expenses, handleCreateOperationsRow, handleDeleteOperationsRow, handleUpdateOperationsRow,
        operatingInfo, operatingPeriodCashFlows, cashFlowPeriods, proForma, showCents,
        handleUpdateProFormaOperations,
    } = useProFormaSocketContext();

    const [operatingExpensesRows, setOperatingExpensesRows] = useState<ProFormaGenericRow[]>([]);
    const [capitalExpendituresRows, setCapitalExpendituresRows] = useState<ProFormaGenericRow[]>([]);

    const {
        tableRef: operatingExpensesTableRef,
        handleTableScroll: handleOperatingExpensesScroll,
        createRefHandler: createOperatingExpensesRefHandler,
    } = useCustomScroll('proforma-expenses-operating', [...OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS]);

    const {
        tableRef: netIncomeTableRef,
        handleTableScroll: handleNetIncomeScroll,
        createRefHandler: createNetIncomeRefHandler,
    } = useCustomScroll('proforma-expenses-net-income', [...OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS]);

    const {
        tableRef: capitalExpendituresTableRef,
        handleTableScroll: handleCapitalExpendituresScroll,
        createRefHandler: createCapitalExpendituresRefHandler,
    } = useCustomScroll('proforma-expenses-capital', [...OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS]);

    const {
        tableRef: cashFlowTableRef,
        handleTableScroll: handleCashFlowScroll,
        createRefHandler: createCashFlowRefHandler,
    } = useCustomScroll('proforma-expenses-cash-flow', [...OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS]);

    const [yearTaxes, setYearTaxes] = useState<number[]>(expenses?.taxPercentagePerYear || []);

    const isIncomeCalculationTypeTenants = operatingInfo?.incomeCalculationType === IncomeCalculationType.TENANTS;

    const handleMillRateChange = (updatedValue: string | number) => {
        const parsedValue = Math.min(Math.max(Number(updatedValue), 0), 1);

        if (operatingInfo?.id) {
            const syntheticEvent = {
                target: {
                    value: parsedValue.toString(),
                },
            } as React.ChangeEvent<HTMLInputElement>;

            handleUpdateOperationsRow(
                OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.UPDATE,
                expenses?.propertyTaxesOperatingExpense?.id || 0,
                'millRate',
            )(syntheticEvent);
        }
    };

    const handleUpdatePropertyTaxPercentage = (propTaxPercentage: number, index: number) => {
        if (!operatingInfo || !operatingInfo.id) return;

        const updatedTaxes = [...yearTaxes];
        updatedTaxes.splice(index, 1, propTaxPercentage);
        setYearTaxes(updatedTaxes);
        handleUpdateProFormaOperations(operatingInfo.id, { taxPercentagePerYear: updatedTaxes });
    };

    const renderPropYearRow = (propTaxPercentage: number, index: number) => (
        <div className="d-flex align-items-center mt-2" key={`prop-tax-year-row-${index}`}>
            <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark">
                Prop tax: year
                {' '}
                {index + 1}
                {' '}
                anticipated % of full
            </NSLabel>
            <NSInputNumberStepper
                name={`propTax_${index}`}
                value={propTaxPercentage}
                onChange={(value: number) => handleUpdatePropertyTaxPercentage(value, index)}
                min={0}
                max={100}
                step={1}
                percentage
                inputGroupClassName="ProFormaPermanentDebt__input-width"
            />
        </div>
    );

    const operatingPeriodColumns = useMemo(() => {
        if (!operatingInfo?.operationBeginMonth) return [];

        return cashFlowPeriods.map((periodStartDate, index) => ({
            key: `column_${periodStartDate}`,
            field: `column_${periodStartDate}`,
            header: (
                <div>
                    <div className="h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-secondary text-right mb-0">
                        MONTH
                        {' '}
                        {index}
                    </div>
                    <div className="h6 text-uppercase text-right text-nowrap mt-1">{format(parseISO(periodStartDate), 'MMM yyyy')}</div>
                </div>
            ),
            format: 'currency' as const,
            isCashFlow: true,
            isFixed: false,
        }));
    }, [cashFlowPeriods, operatingInfo?.operationBeginMonth]);

    const getColumnDropdown = (tableName: string) => ({
        label: 'From:',
        options: CAPITAL_RESERVES_OPTIONS,
        getSelectedOption: (row: ProFormaGenericRow) => {
            if (row.operationExpenseType === OperationExpenseType.CAPITAL_RESERVES) {
                return CAPITAL_RESERVES_OPTIONS.find(option => option.value === tableName);
            }
            return undefined;
        },
        handleSelectOption: (rowId: string) => (option: IOptionAnyValue | null) => {
            if (option && option?.value !== tableName) {
                const isSelectingOpex = option.value === OPERATING_EXPENSES_LABEL;
                const handlerName = isSelectingOpex ? OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.DELETE : OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.DELETE;
                handleDeleteOperationsRow(handlerName, Number(rowId));
            }
        },
        shouldShowDropdown: (row: ProFormaGenericRow) => row.operationExpenseType === OperationExpenseType.CAPITAL_RESERVES,
        isReadOnly: true,
    });

    const capitalExpenditurePeriods = useMemo(() => getPeriods(
        cashFlowPeriods,
        operatingPeriodCashFlows,
        expenses?.capitalExpenditures || [],
        'capitalExpenditures',
        operatingInfo?.operationBeginMonth,
        !operatingInfo?.operationBeginMonth,
    ), [proForma, cashFlowPeriods, operatingPeriodCashFlows, expenses?.capitalExpenditures]);

    const operatingExpensePeriods = useMemo(() => getPeriods(
        cashFlowPeriods,
        operatingPeriodCashFlows,
        expenses?.operatingExpenses || [],
        'operatingExpenses',
        operatingInfo?.operationBeginMonth,
        !operatingInfo?.operationBeginMonth,
    ), [expenses, cashFlowPeriods, operatingPeriodCashFlows, expenses?.operatingExpenses]);

    const capitalExpendituresColumns = useMemo(() => {
        const baseCols = [
            {
                ...CAPITAL_EXPENDITURES_COLUMNS[0],
                isFixed: true,
                dropdown: getColumnDropdown(CAPITAL_EXPENDITURES_LABEL),
                isReadOnly: (row: ProFormaGenericRow) => row.operationExpenseType === OperationExpenseType.CAPITAL_RESERVES
                    || row.operationExpenseType === OperationExpenseType.LEASING_COST_RESERVE,
            },
            ...CAPITAL_EXPENDITURES_COLUMNS.slice(1).map(col => {
                if (col.key === 'costPerUnitPerYear') {
                    const { key, field, header } = transformCostPerUnitColumn(proForma.proFormaModelId, isIncomeCalculationTypeTenants);
                    return {
                        ...col,
                        key,
                        field,
                        header,
                    };
                }
                return col;
            }),
        ];

        return [...baseCols, ...operatingPeriodColumns];
    }, [operatingPeriodColumns, proForma.proFormaModelId]);

    const operatingExpensesColumns = useMemo(() => {
        const baseCols = [
            {
                ...OPERATING_EXPENSES_COLUMNS[0],
                isFixed: true,
                dropdown: getColumnDropdown(OPERATING_EXPENSES_LABEL),
                isReadOnly: (row: ProFormaGenericRow) => row.operationExpenseType === OperationExpenseType.CAPITAL_RESERVES
                    || row.operationExpenseType === OperationExpenseType.PROPERTY_TAXES,
                customCell: {
                    shouldShowCustomCell: (row: ProFormaGenericRow) => row.operationExpenseType === OperationExpenseType.PROPERTY_TAXES,
                    renderCell: () => (
                        <div className="d-flex flex-row align-items-center justify-content-between">
                            <span className="text-white mr-2">Property taxes</span>
                            <div className="ProFormaExpenses__custom-cell d-flex flex-row aling-items-center py-1 px-2 ml-2">
                                <div className="pr-1 text-white">Mill rate:</div>
                                <NSCellInput
                                    type="number"
                                    numberType="percentage"
                                    value={expenses?.propertyTaxesOperatingExpense?.millRate || 0}
                                    handleUpdateValue={handleMillRateChange}
                                    placeholder="Enter percentage"
                                    isRightAligned
                                    inputHeight="26px"
                                    inputWidth="54px"
                                />
                            </div>
                        </div>
                    ),
                },
            },
            ...OPERATING_EXPENSES_COLUMNS.slice(1).map(col => {
                if (col.key === 'costPerUnitPerYear') {
                    const { key, field, header } = transformCostPerUnitColumn(proForma.proFormaModelId, isIncomeCalculationTypeTenants);
                    return {
                        ...col,
                        key,
                        field,
                        header,
                    };
                }
                return col;
            }),
        ];

        return [...baseCols, ...operatingPeriodColumns];
    }, [operatingPeriodColumns, expenses?.propertyTaxesOperatingExpense?.millRate, proForma.proFormaModelId]);

    const netOperatingIncomeColumns = useMemo(() => {
        const baseCols = NET_OPERATION_INCOME_COLUMNS.map(col => {
            let { header } = col;
            if (col.key === 'totalAmountPerYear') {
                header = formatCurrencyWithParentheses(expenses?.netOperatingIncome || 0, showCents);
            }
            return { ...col, header };
        });

        const operatingCols = operatingPeriodColumns.map((col, index) => {
            const cashFlowPeriod = operatingPeriodCashFlows[index];
            return {
                ...col,
                header: formatCurrencyWithParentheses(
                    cashFlowPeriod?.netOperatingIncome !== null ? Number(cashFlowPeriod?.netOperatingIncome) : 0, showCents,
                ),
            };
        });

        return [...baseCols, ...operatingCols];
    }, [expenses?.netOperatingIncome, operatingPeriodColumns, operatingPeriodCashFlows, showCents]);

    const totalCashFlowOperationsColumns = useMemo(() => {
        const baseCols = CASH_FLOW_COLUMNS.map(col => {
            let { header } = col;
            if (col.key === 'totalAmountPerYear') {
                header = formatCurrencyWithParentheses(expenses?.totalCashFlowOperations ? expenses.totalCashFlowOperations : 0, showCents);
            }
            return { ...col, header };
        });

        const operatingCols = operatingPeriodColumns.map((col, index) => {
            const cashFlowPeriod = operatingPeriodCashFlows[index];
            return {
                ...col,
                header: formatCurrencyWithParentheses(
                    cashFlowPeriod?.cashFlowFromOperations !== null ? Number(cashFlowPeriod?.cashFlowFromOperations) : 0, showCents,
                ),
            };
        });

        return [...baseCols, ...operatingCols];
    }, [expenses?.totalCashFlowOperations, operatingPeriodColumns, operatingPeriodCashFlows, showCents]);

    const isOfficeIndustrialRetail = proForma.proFormaModelId === 2 || proForma.proFormaModelId === 3 || proForma.proFormaModelId === 4;
    const maxWidth = isOfficeIndustrialRetail ? '800px' : '720px';

    useEffect(() => {
        if (expenses) {
            setYearTaxes(expenses.taxPercentagePerYear || []);
            if (expenses.operatingExpenses) {
                setOperatingExpensesRows(prevRows => {
                    const existingRowsMap = new Map(prevRows.map((row: ProFormaGenericRow) => [row.id, row]));
                    const rows = (expenses.operatingExpenses || []).filter(op => op != null).map(operatingExpense => {
                        const existingRow = existingRowsMap.get(String(operatingExpense.id));
                        let emptyValue = '-';

                        if (operatingInfo?.operationBeginMonth !== null && operatingInfo?.operationBeginMonth !== undefined) {
                            const cashFlowValues = operatingPeriodCashFlows[operatingInfo?.operationBeginMonth]?.operatingExpenses;
                            const desiredValue = cashFlowValues?.find(v => v.name === operatingExpense.name)?.value;
                            emptyValue = desiredValue !== null && desiredValue !== undefined ? '0' : '-';
                        }
                        const operatingPeriodData = Object.fromEntries(
                            (cashFlowPeriods || []).map((periodStartDate: string, index: number) => {
                                const cashFlowPeriod = operatingPeriodCashFlows[index];
                                const expenseValue = cashFlowPeriod?.operatingExpenses?.find(
                                    (exp: INameValue) => exp.name === operatingExpense.name,
                                )?.value;
                                return [
                                    `column_${periodStartDate}`,
                                    expenseValue !== null && expenseValue !== undefined ? String(expenseValue) : emptyValue,
                                ];
                            }),
                        );

                        return {
                            ...(existingRow || {}),
                            id: String(operatingExpense?.id),
                            name: operatingExpense.name || '',
                            annualGrowth: operatingExpense.annualGrowth,
                            percentOfEffectiveGrossRevenue: operatingExpense.percentOfEffectiveGrossRevenue,
                            costPerUnitPerYear: operatingExpense.costPerUnitPerYear,
                            valuePerSfPerYear: operatingExpense.valuePerSfPerYear,
                            totalAmountPerYear: operatingExpense.totalAmountPerYear,
                            operationExpenseType: operatingExpense.operationExpenseType,
                            isDeleteAllowed: operatingExpense.isDeleteAllowed,
                            ...createCashFlowData(operatingExpense.cashFlow || []),
                            ...operatingPeriodData,
                        };
                    });

                    if (expenses.propertyTaxesOperatingExpense) {
                        const operatingExpense = expenses.propertyTaxesOperatingExpense;
                        const existingRow = existingRowsMap.get(String(operatingExpense.id));
                        let emptyValue = '-';

                        if (operatingInfo?.operationBeginMonth !== null && operatingInfo?.operationBeginMonth !== undefined) {
                            const propertyTaxesOperatingExpenseCashFlow
                                = operatingPeriodCashFlows[operatingInfo?.operationBeginMonth]?.propertyTaxesOperatingExpense?.value;
                            emptyValue = propertyTaxesOperatingExpenseCashFlow !== null && propertyTaxesOperatingExpenseCashFlow !== undefined ? '0' : '-';
                        }
                        const operatingPeriodData = Object.fromEntries(
                            (cashFlowPeriods || []).map((periodStartDate: string, index: number) => {
                                const cashFlowPeriod = operatingPeriodCashFlows[index];
                                return [
                                    `column_${periodStartDate}`,
                                    cashFlowPeriod?.propertyTaxesOperatingExpense?.value !== null
                                        && cashFlowPeriod?.propertyTaxesOperatingExpense?.value !== undefined
                                        ? String(cashFlowPeriod.propertyTaxesOperatingExpense.value) : emptyValue,
                                ];
                            }),
                        );

                        rows.push({
                            ...(existingRow || {}),
                            id: String(operatingExpense?.id),
                            name: operatingExpense.name || '',
                            annualGrowth: operatingExpense.annualGrowth,
                            percentOfEffectiveGrossRevenue: operatingExpense.percentOfEffectiveGrossRevenue,
                            costPerUnitPerYear: operatingExpense.costPerUnitPerYear,
                            valuePerSfPerYear: operatingExpense.valuePerSfPerYear,
                            totalAmountPerYear: operatingExpense.totalAmountPerYear,
                            operationExpenseType: operatingExpense.operationExpenseType,
                            isDeleteAllowed: operatingExpense.isDeleteAllowed,
                            ...createCashFlowData(operatingExpense.cashFlow || []),
                            ...operatingPeriodData,
                        });
                    }
                    return rows;
                });
            }

            if (expenses.capitalExpenditures) {
                setCapitalExpendituresRows(prevRows => {
                    const existingRowsMap = new Map(prevRows.map((row: ProFormaGenericRow) => [row?.id || 0, row]));

                    return (expenses.capitalExpenditures || [])
                        .filter(capex => capex != null)
                        .map(capital => {
                            const existingRow = existingRowsMap.get(String(capital.id));
                            let emptyValue = '-';

                            if (operatingInfo?.operationBeginMonth !== null && operatingInfo?.operationBeginMonth !== undefined) {
                                const cashFlowValues = operatingPeriodCashFlows[operatingInfo?.operationBeginMonth]?.capitalExpenditures;
                                const desiredValue = cashFlowValues?.find(v => v.name === capital.name)?.value;
                                emptyValue = desiredValue !== null && desiredValue !== undefined ? '0' : '-';
                            }
                            const operatingPeriodData = Object.fromEntries(
                                (cashFlowPeriods || []).map((periodStartDate: string, index: number) => {
                                    const cashFlowPeriod = operatingPeriodCashFlows[index];
                                    const capexValue = cashFlowPeriod?.capitalExpenditures?.find(
                                        (capex: INameValue) => capex.name === capital.name,
                                    )?.value;
                                    return [
                                        `column_${periodStartDate}`,
                                        capexValue !== null && capexValue !== undefined ? String(capexValue) : emptyValue,
                                    ];
                                }),
                            );

                            return {
                                ...(existingRow || {}),
                                id: String(capital?.id),
                                name: capital.name || '',
                                annualGrowth: capital.annualGrowth,
                                percentOfEffectiveGrossRevenue: capital.percentOfEffectiveGrossRevenue,
                                costPerUnitPerYear: capital.costPerUnitPerYear,
                                valuePerSfPerYear: capital.valuePerSfPerYear,
                                totalAmountPerYear: capital.totalAmountPerYear,
                                operationExpenseType: capital.operationExpenseType,
                                isDeleteAllowed: capital.isDeleteAllowed,
                                ...createCashFlowData(capital.cashFlow || []),
                                ...operatingPeriodData,
                            };
                        });
                });
            }
        }
    }, [expenses, cashFlowPeriods, operatingPeriodCashFlows]);

    return (
        <div className="ProFormaExpenses d-flex flex-column pb-3">
            <RenderIf isTrue={yearTaxes.length}>
                <div className="ProFormaExpenses__inputs-container w-50 p-3">
                    {yearTaxes.map((item, index) => renderPropYearRow(item, index))}
                </div>
                <div className="ProFormaExpenses__separator mx-3" />
            </RenderIf>
            <div className="p-3">
                <ProFormaGenericTable
                    useLargerCells
                    columns={operatingExpensesColumns}
                    rows={operatingExpensesRows}
                    setRows={setOperatingExpensesRows}
                    handleAddRow={() => handleCreateOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.CREATE)}
                    handleDeleteRow={rowId => handleDeleteOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.DELETE, rowId)}
                    handleUpdateRow={(rowId, dataKey) => handleUpdateOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.UPDATE, rowId, dataKey)}
                    onScrollableRef={createOperatingExpensesRefHandler(operatingExpensesTableRef)}
                    onScroll={() => handleOperatingExpensesScroll('table')}
                    fixedColumnsMaxWidth={maxWidth}
                    firstColumnWidth="250px"
                    thirdColumnWidth="100px"
                    fifthColumnWidth="120px"
                    numFirstColumns={1}
                    totalText="Total operating expenses"
                    totalValues={expenses?.totalOperatingExpenses ? {
                        annualGrowth: String(expenses.totalOperatingExpenses.totalAnnualGrowth || 0),
                        percentOfEffectiveGrossRevenue: String(expenses.totalOperatingExpenses.totalPercentOfEffectiveGrossRevenue || 0),
                        costPerUnitPerYear: String(expenses.totalOperatingExpenses.totalCostPerUnitPerYear || 0),
                        valuePerSfPerYear: String(expenses.totalOperatingExpenses.totalValuePerSfPerYear || 0),
                        totalAmountPerYear: String(expenses.totalOperatingExpenses.totalAmountPerYear || 0),
                        ...createCashFlowData(expenses.totalOperatingExpenses.totalCashFlow || []),
                        ...Object.fromEntries(
                            operatingExpensePeriods.map(period => {
                                const periodIndex = cashFlowPeriods?.indexOf(period.periodDate) ?? -1;
                                const cashFlow = periodIndex >= 0 ? operatingPeriodCashFlows[periodIndex] : null;
                                return [
                                    `column_${period.periodDate}`,
                                    cashFlow?.totalOperatingExpenses !== null ? String(cashFlow?.totalOperatingExpenses || 0) : '0',
                                ];
                            }),
                        ),
                    } : undefined}
                />
                <div className="pb-1" />
                <ProFormaGenericTable
                    fixedColumnsMaxWidth={maxWidth}
                    columns={netOperatingIncomeColumns}
                    rows={[]}
                    setRows={() => { }}
                    hideTotalFooter
                    hideAddButton
                    onScrollableRef={createNetIncomeRefHandler(netIncomeTableRef)}
                    onScroll={() => handleNetIncomeScroll('table')}
                    onlyTotals
                />
                <div className="pb-1" />
                <ProFormaGenericTable
                    columns={capitalExpendituresColumns}
                    rows={capitalExpendituresRows}
                    setRows={setCapitalExpendituresRows}
                    handleAddRow={() => handleCreateOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.CREATE)}
                    handleDeleteRow={rowId => handleDeleteOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.DELETE, rowId)}
                    handleUpdateRow={(rowId, dataKey) => handleUpdateOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.UPDATE, rowId, dataKey)}
                    onScrollableRef={createCapitalExpendituresRefHandler(capitalExpendituresTableRef)}
                    onScroll={() => handleCapitalExpendituresScroll('table')}
                    fixedColumnsMaxWidth={maxWidth}
                    firstColumnWidth="250px"
                    thirdColumnWidth="100px"
                    fifthColumnWidth="120px"
                    numFirstColumns={1}
                    useLargerCells
                    totalText="Total capital expenditures"
                    totalValues={expenses?.totalCapitalExpenditures ? {
                        annualGrowth: String(expenses.totalCapitalExpenditures.totalAnnualGrowth || 0),
                        percentOfEffectiveGrossRevenue: String(expenses.totalCapitalExpenditures.totalPercentOfEffectiveGrossRevenue || 0),
                        costPerUnitPerYear: String(expenses.totalCapitalExpenditures.totalCostPerUnitPerYear || 0),
                        valuePerSfPerYear: String(expenses.totalCapitalExpenditures.totalValuePerSfPerYear || 0),
                        totalAmountPerYear: String(expenses.totalCapitalExpenditures.totalAmountPerYear || 0),
                        ...createCashFlowData(expenses.totalCapitalExpenditures.totalCashFlow || []),
                        ...Object.fromEntries(
                            capitalExpenditurePeriods.map(period => {
                                const periodIndex = cashFlowPeriods?.indexOf(period.periodDate) ?? -1;
                                const cashFlow = periodIndex >= 0 ? operatingPeriodCashFlows[periodIndex] : null;
                                return [
                                    `column_${period.periodDate}`,
                                    cashFlow?.totalCapitalExpenditures !== null ? String(cashFlow?.totalCapitalExpenditures || 0) : '0',
                                ];
                            }),
                        ),
                    } : undefined}
                />
                <div className="pb-1" />
                <ProFormaGenericTable
                    fixedColumnsMaxWidth={maxWidth}
                    columns={totalCashFlowOperationsColumns}
                    rows={[]}
                    setRows={() => { }}
                    hideTotalFooter
                    hideAddButton
                    onScrollableRef={createCashFlowRefHandler(cashFlowTableRef)}
                    onScroll={() => handleCashFlowScroll('table')}
                    onlyTotals
                />
            </div>
        </div>
    );
};

export default ProFormaExpenses;
